import React from "react";
import moment from "moment";
import { useHistory, useParams } from "react-router-dom";
import AllNflGamesHelper from "../../classes/allNflGamesHelper";
import WaiversPlayersHelper from "../../classes/waiversPlayersHelper";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useNflGamesForWeek from "../../hooks/queryResults/useNflGamesForWeek";
import useRosters from "../../hooks/queryResults/useRosters";
import useTeams from "../../hooks/queryResults/useTeams";
import useUserTeam from "../../hooks/queryResults/useUserTeam";
import useWaiversPlayers from "../../hooks/queryResults/useWaiversPlayers";
import useWeek from "../../hooks/queryResults/useWeek";
import useAdminMode from "../../hooks/useAdminMode";
import NflUtil from "../../util/nflUtil";
import { getPlayerName } from "../player/PlayerName";
import TeamLogo from "../team/TeamLogo";
import Loading from "../ui/Loading";
import Pagination from "../ui/Pagination";

export default function PlayersTable({
  players,
  playersToShow,
  pageSize,
  onClickAdd,
  onClickDrop,
  onClickTrade,
}) {
  if (!pageSize) pageSize = 50;

  let { league: leagueUrl, season: seasonUrl, page: pageUrl } = useParams();
  const userTeam = useUserTeam();
  const history = useHistory();
  const { rostersData, rostersQuery } = useRosters();
  const { currentWeek } = useWeek();
  const { nflGamesData, nflGamesQuery } = useNflGamesForWeek(
    seasonUrl,
    currentWeek
  );
  const { waiversPlayersData, waiversPlayersQuery } = useWaiversPlayers();
  const { teams, teamsQuery } = useTeams();
  const adminMode = useAdminMode();

  let page;
  if (pageUrl) page = parseInt(pageUrl, 10);
  else page = 1;

  const isLoading = useIsLoading(
    rostersData,
    rostersQuery,
    teams,
    teamsQuery,
    userTeam,
    waiversPlayersData,
    waiversPlayersQuery,
    nflGamesData,
    nflGamesQuery
  );
  if (isLoading) return <Loading />;

  if (players.length === 0) return <p>No players found.</p>;

  const allNflGamesHelper = new AllNflGamesHelper(nflGamesData);
  const waiversPlayersHelper = new WaiversPlayersHelper(waiversPlayersData);

  const handleAction = (actionType, playerIn, playerOut) => {
    const playersIn = [];
    const playersOut = [];
    if (playerIn) playersIn.push(playerIn);
    if (playerOut) playersOut.push(playerOut);

    history.push({
      pathname: `/${leagueUrl}/${seasonUrl}/${userTeam.idOrSlug}/roster`,
      state: { actionType, playersIn, playersOut },
    });
  };

  return (
    <div>
      <table>
        <thead>
          <tr>
            <th>Action</th>
            <th>Status</th>
            <th title={"Fantasy Team"}>Tm</th>
            <th>Pos</th>
            <th>Name</th>
            <th>NFL</th>
            <th>Opp</th>
          </tr>
        </thead>
        <tbody>
          {playersToShow.map((player) => {
            let teamCell = null;
            let nflTeam = null;
            let opp = null;
            let actionButton;
            let status = null;

            const pos = player.pos;
            const playerName = getPlayerName(player, { injury: 1 });

            let teamId = null;
            let team = null;
            if (rostersData.rosters.players.hasOwnProperty(player.id)) {
              teamId = rostersData.rosters.players[player.id];
              team = teams.getTeamById(teamId);
            }
            const isTaken = !!teamId;

            if (isTaken) teamCell = <TeamLogo team={team} size={"sm"} />;

            if (!isTaken) {
              if (waiversPlayersHelper.isOnWaivers(player.id)) {
                const clearDueDate = waiversPlayersHelper.getClearDueDate(
                  player.id
                );
                const clearDueDateFormatted =
                  moment(clearDueDate).format("ddd M/D h:mm a");
                status = (
                  <span title={"On waivers until " + clearDueDateFormatted}>
                    W
                  </span>
                );
              }
            }

            if (player.nflTeamAbbr) {
              nflTeam = NflUtil.formatNflTeamAbbr(player.nflTeamAbbr);
              opp = allNflGamesHelper.getMatchupText(
                currentWeek,
                player.nflTeamAbbr
              );
            }

            const handleAdd = onClickAdd
              ? () => onClickAdd(player.id)
              : () => handleAction("add", player.id);

            const handleDrop = onClickDrop
              ? () => onClickDrop(player.id)
              : () => handleAction("drop", null, player.id);

            const handleTrade = onClickTrade
              ? () => onClickTrade(player.id)
              : () => handleAction("trade", player.id);

            const dropButton = (
              <button className={"btn"} onClick={handleDrop}>
                Drop
              </button>
            );

            const tradeButton = (
              <button className={"btn"} onClick={handleTrade} disabled={true}>
                Trade
              </button>
            );

            const addButton = (
              <button className={"btn"} onClick={handleAdd}>
                Add
              </button>
            );

            if (isTaken) {
              if (teamId === userTeam.obj.id) {
                actionButton = dropButton;
              } else {
                actionButton = adminMode ? (
                  <>
                    <span>{tradeButton}</span>
                    <span style={{ marginLeft: "0.25rem" }}>{dropButton}</span>
                  </>
                ) : null;
              }
            } else {
              actionButton = addButton;
            }

            return (
              <tr key={player.id}>
                <td>{actionButton}</td>
                <td>{status}</td>
                <td>{teamCell}</td>
                <td>{pos}</td>
                <td className={"left"}>{playerName}</td>
                <td className={"left"}>{nflTeam}</td>
                <td className={"left"}>{opp}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        totalCount={players.length}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={(page) => {
          history.push(`/${leagueUrl}/${seasonUrl}/players/${page}`);
        }}
      />
      <p style={{ opacity: 0.5, textAlign: "center" }}>
        {pageSize >= players.length
          ? `Showing all ${players.length} players.`
          : `Showing ${pageSize} of ${players.length} players.`}
      </p>
    </div>
  );
}
